// ROOM 404: Centralized Authentication & Membership Context

import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiRequest, getStoredToken, setStoredToken } from '../services/api';
import { supabase } from '../services/supabase';
import type { UserRole, UserSession } from '../types';

interface AuthContextValue {
  session: UserSession | null;
  loading: boolean;
  token: string | null;
  isAuthenticated: boolean;
  isVerifiedMember: boolean;
  isStaff: boolean;
  isVerifier: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  refreshSession: () => Promise<void>;
  loginWithEmail: (email: string) => Promise<{ error: any; demoMode?: boolean }>;
  verifyOtpCode: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  setCustomToken: (jwt: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<UserSession | null>(null);
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [loading, setLoading] = useState<boolean>(true);

  const fetchSession = async (jwtToken?: string): Promise<UserSession | null> => {
    const activeToken = jwtToken || token;
    if (!activeToken) {
      setSession(null);
      return null;
    }

    try {
      const data = await apiRequest<{
        authenticated: boolean;
        registered: boolean;
        session?: UserSession;
        supabaseUid?: string;
        email?: string;
      }>('/api/auth/session', { token: activeToken });

      if (data.registered && data.session) {
        setSession(data.session);
        return data.session;
      } else if (data.authenticated) {
        // Authenticated with Supabase, but not yet registered in D1
        const interimSession: UserSession = {
          userId: 'pending',
          supabaseUid: data.supabaseUid || '',
          emailMasked: data.email || 'anonymous',
          status: 'applicant',
          roles: ['applicant'],
        };
        setSession(interimSession);
        return interimSession;
      } else {
        setSession(null);
        return null;
      }
    } catch {
      setSession(null);
      return null;
    }
  };

  useEffect(() => {
    // Initial check from localStorage or Supabase
    supabase.auth.getSession().then(({ data }) => {
      const sbToken = data?.session?.access_token || getStoredToken();
      if (sbToken) {
        setToken(sbToken);
        setStoredToken(sbToken);
        fetchSession(sbToken).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, sbSession) => {
      if (sbSession?.access_token) {
        setToken(sbSession.access_token);
        setStoredToken(sbSession.access_token);
        await fetchSession(sbSession.access_token);
      } else if (!getStoredToken()) {
        setSession(null);
        setToken(null);
      }
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const refreshSession = async () => {
    await fetchSession();
  };

  const loginWithEmail = async (email: string) => {
    const { error, demoMode } = await (await import('../services/supabase')).sendEmailOtp(email);
    return { error, demoMode };
  };

  const verifyOtpCode = async (email: string, code: string) => {
    try {
      const { data, error } = await (await import('../services/supabase')).verifyEmailOtp(email, code);
      if (error || !data?.session?.access_token) {
        return { success: false, error: error?.message || 'Invalid OTP verification code' };
      }

      const jwt = data.session.access_token;
      setToken(jwt);
      setStoredToken(jwt);
      await fetchSession(jwt);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'OTP verification failed' };
    }
  };

  const setCustomToken = async (jwt: string) => {
    setToken(jwt);
    setStoredToken(jwt);
    await fetchSession(jwt);
  };

  const logout = () => {
    supabase.auth.signOut().catch(() => {});
    setStoredToken(null);
    setToken(null);
    setSession(null);
  };

  const roles = session?.roles || [];
  const hasRole = (role: UserRole) => roles.includes(role) || roles.includes('owner');

  const value: AuthContextValue = {
    session,
    loading,
    token,
    isAuthenticated: Boolean(session),
    isVerifiedMember: session?.status === 'member',
    isStaff: roles.some((r) => ['moderator', 'administrator', 'owner', 'verifier'].includes(r)),
    isVerifier: hasRole('verifier') || hasRole('administrator'),
    isAdmin: hasRole('administrator'),
    isOwner: roles.includes('owner'),
    refreshSession,
    loginWithEmail,
    verifyOtpCode,
    setCustomToken,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
